/**
 * Unlocks the shared AudioContext on the first user interaction
 * and provides a pool of <audio> elements for remote-feed playback.
 */

import { formatErrorForLogging, logger } from 'utils/logger';
import * as Sentry from '@sentry/react';
import { AudioElement } from 'utils/webrtc/types';
import { store } from 'store/store';
import { selectIsGlobalAudioMuted } from 'features/application/applicationSlice';

export class UnlockedAudio extends Audio {
  unlocked: boolean = false;

  unloaded: boolean = true;

  // Mirrors WebKit's one-shot RequirePageConsentToLoadMedia removal — set once, never reset.
  hasEverLoaded: boolean = false;

  constructor() {
    super();
    this.addEventListener(
      'loadeddata',
      () => {
        this.hasEverLoaded = true;
      },
      { once: true }
    );
  }
}

export class Pool {
  ctx: AudioContext = new AudioContext();

  isUnlocked: boolean = false;

  pool: UnlockedAudio[] = [];

  poolSize: number = 1000;

  scratchBuffer?: AudioBuffer;

  globalMute: boolean = false;

  usedElements: Record<string, AudioElement> = {};

  tail: number = 0;

  // ownerId is an opaque key (userId) — the pool knows nothing about users/Redux.
  private ownerByElementId: Record<string, string> = {};

  private locallyMutedOwners: Set<string> = new Set();

  private unlockAttempts: number = 0;

  // Each touch gesture fires both touchend + click, so 6 attempts ≈ 3 real gestures
  private maxUnlockAttempts: number = 6;

  private releaseElement = (audio: UnlockedAudio) => {
    if (audio.unlocked) {
      this.pool.push(audio);

      logger
        .remote({ system: true, capture: 'audioPool' })
        .debug('AudioPool: element released back to pool', {
          elementId: audio.id,
          poolSize: this.pool.length,
          usedElements: Object.keys(this.usedElements).length,
        });
    } else {
      logger
        .remote({ system: true, capture: 'audioPool' })
        .warn('AudioPool: attempted to release non-unlocked element', {
          elementId: audio.id,
          unlocked: audio.unlocked,
        });
    }
  };

  private generatePool = () => {
    // Create a pool of unlocked HTML5 Audio objects
    while (this.pool.length < this.poolSize) {
      try {
        const audio = new UnlockedAudio();

        // Mark this Audio object as unlocked to ensure it can get returned
        // to the unlocked pool when released.
        audio.unlocked = true;

        audio.id = `unlocked-audio-${this.tail}`;
        this.tail += 1;

        this.pool.push(audio);
      } catch (error) {
        // Break silently - final pool size will be visible in success log
        break;
      }
    }
  };

  prewarmLoadGate = () => {
    // A bare load() while visible permanently clears WebKit's one-shot media-load gate.
    // Hidden, it only parks a listener that fires on return, so the flag must not latch then.
    const inWindow = document.visibilityState === 'visible';
    const startedAt = performance.now();
    let loadedElements = 0;
    let failedElements = 0;
    let firstError: Record<string, unknown> | undefined;

    for (const audio of this.pool) {
      if (!audio.hasEverLoaded) {
        try {
          audio.load();
          loadedElements += 1;
          if (inWindow) {
            audio.hasEverLoaded = true;
          }
        } catch (error) {
          failedElements += 1;
          firstError ??= formatErrorForLogging(error);
        }
      }
    }

    logger.remote({ system: true, capture: 'audioPool' }).info('AudioPool: load gate prewarmed', {
      loadedElements,
      failedElements,
      firstError,
      inWindow,
      poolSize: this.pool.length,
      durationMs: Math.round(performance.now() - startedAt),
    });
  };

  private unlock = () => {
    if (this.isUnlocked) {
      return;
    }

    this.unlockAttempts += 1;

    logger
      .remote({ system: true, capture: 'audioPool' })
      .info('AudioPool: attempting to unlock audio', {
        attempt: this.unlockAttempts,
        contextState: this.ctx.state,
        poolSize: this.pool.length,
      });

    try {
      // Scratch buffer to prevent memory leaks on iOS.
      // See: https://stackoverflow.com/questions/24119684/web-audio-api-memory-leaks-on-mobile-platforms
      this.scratchBuffer = this.ctx.createBuffer(1, 1, 22050);

      this.generatePool();
      this.prewarmLoadGate();

      const source = this.ctx.createBufferSource();
      source.buffer = this.scratchBuffer;
      source.connect(this.ctx.destination);

      const checkUnlockSuccess = () => {
        const contextRunning = this.ctx.state === 'running';
        const hasUnlockedAudio = this.pool.length > 0;

        if (contextRunning && hasUnlockedAudio) {
          this.completeUnlock(source);
          return true;
        }
        return false;
      };

      source.onended = () => {
        this.completeUnlock(source);
      };

      source.start(0);

      // Check immediately after resume promise resolves
      this.ctx
        .resume()
        .then(() => {
          logger
            .remote({ system: true, capture: 'audioPool' })
            .info('AudioPool: audio context resumed successfully', {
              attempt: this.unlockAttempts,
              contextState: this.ctx.state,
            });

          setTimeout(() => {
            if (!this.isUnlocked && !checkUnlockSuccess()) {
              this.completeUnlock(source);
            }
          }, 500);
        })
        .catch((error) => {
          logger
            .remote({ system: true, capture: 'audioPool' })
            .error('AudioPool: failed to resume audio context', {
              ...formatErrorForLogging(error),
              contextState: this.ctx.state,
              attempt: this.unlockAttempts,
            });
        });
    } catch (error) {
      this.handleUnlockError(error);
    }
  };

  private completeUnlock = (source: AudioBufferSourceNode) => {
    if (this.isUnlocked) {
      return;
    }

    try {
      source.disconnect(0);
    } catch (error) {
      // Ignore disconnect errors
    }

    this.isUnlocked = true;

    document.removeEventListener('touchend', this.unlock, true);
    document.removeEventListener('click', this.unlock, true);
    document.removeEventListener('keydown', this.unlock, true);

    logger
      .remote({ system: true, capture: 'audioPool' })
      .info('AudioPool: auto unlock completed successfully', {
        attempt: this.unlockAttempts,
        ...this.getState(),
      });
  };

  private handleUnlockError = (error: unknown) => {
    logger
      .remote({ system: true, capture: 'audioPool' })
      .error('AudioPool: unlock process failed', {
        ...formatErrorForLogging(error),
        attempt: this.unlockAttempts,
        contextState: this.ctx.state,
      });

    if (this.unlockAttempts < this.maxUnlockAttempts) {
      logger
        .remote({ system: true, capture: 'audioPool' })
        .debug('AudioPool: will retry unlock on next user interaction', {
          currentAttempt: this.unlockAttempts,
          maxAttempts: this.maxUnlockAttempts,
        });
    } else {
      logger
        .remote({ system: true, capture: 'audioPool' })
        .error('AudioPool: max unlock attempts reached, giving up', {
          attempts: this.unlockAttempts,
          maxAttempts: this.maxUnlockAttempts,
        });

      Sentry.captureException(error, {
        tags: {
          component: 'AudioPool',
          operation: 'unlock',
        },
        extra: {
          totalAttempts: this.unlockAttempts,
          maxAttempts: this.maxUnlockAttempts,
          contextState: this.ctx.state,
          userAgent: navigator.userAgent,
          lastError: formatErrorForLogging(error),
        },
      });

      document.removeEventListener('touchend', this.unlock, true);
      document.removeEventListener('click', this.unlock, true);
      document.removeEventListener('keydown', this.unlock, true);
    }
  };

  initialize = () => {
    if (this.isUnlocked) {
      return;
    }

    logger
      .remote({ system: true, capture: 'audioPool' })
      .log('AudioPool: initializing auto unlock system', {
        contextState: this.ctx.state,
        unlockAttempts: this.unlockAttempts,
      });

    document.addEventListener('touchend', this.unlock, true);
    document.addEventListener('click', this.unlock, true);
    document.addEventListener('keydown', this.unlock, true);
  };

  assignOwner = (element: UnlockedAudio | HTMLAudioElement, ownerId: string | undefined) => {
    if (ownerId) {
      this.ownerByElementId[element.id] = ownerId;
    } else {
      delete this.ownerByElementId[element.id];
    }
    this.applyPolicyToElement(element);
  };

  setLocallyMutedOwners = (ownerIds: string[]) => {
    this.locallyMutedOwners = new Set(ownerIds);
    this.applyPolicy();
  };

  private applyPolicyToElement = (element: UnlockedAudio | HTMLAudioElement) => {
    const owner = this.ownerByElementId[element.id];
    const muted = this.globalMute || (!!owner && this.locallyMutedOwners.has(owner));
    element.muted = muted;
    element.volume = muted ? 0 : 1;
  };

  private applyPolicy = () => {
    for (const element of Object.values(this.usedElements)) {
      if (element) {
        this.applyPolicyToElement(element);
      }
    }
  };

  obtainElement = async (): Promise<UnlockedAudio | HTMLAudioElement | null> => {
    logger.remote({ system: true, capture: 'audioPool' }).debug('AudioPool: obtaining element', {
      poolSize: this.pool.length,
      usedElements: Object.keys(this.usedElements).length,
      contextState: this.ctx.state,
      isUnlocked: this.isUnlocked,
      globalMute: this.globalMute,
    });

    if (this.pool.length) {
      const element = this.pool.pop();
      if (!element) {
        logger
          .remote({ system: true, capture: 'audioPool' })
          .warn('AudioPool: pool.pop() returned null/undefined', {
            poolSize: this.pool.length,
          });
        return null;
      }

      element.unloaded = false;

      // make sure the AudioContext isn't suspended, and resume it if it is.
      if (this.ctx.state === 'suspended') {
        logger
          .remote({ system: true, capture: 'audioPool' })
          .debug('AudioPool: audio context is suspended, attempting to resume', {
            contextState: this.ctx.state,
            elementId: element.id,
          });

        try {
          await this.ctx.resume();
          logger
            .remote({ system: true, capture: 'audioPool' })
            .info('AudioPool: audio context resumed successfully during element obtain', {
              contextState: this.ctx.state,
              elementId: element.id,
            });
        } catch (error) {
          logger
            .remote({ system: true, capture: 'audioPool' })
            .error('AudioPool: failed to resume audio context during element obtain', {
              ...formatErrorForLogging(error),
              contextState: this.ctx.state,
              elementId: element.id,
            });

          // Continue anyway, element might still work
        }
      }

      this.usedElements[element.id] = element;

      // Check for global mute state inconsistency
      const reduxGlobalMute = selectIsGlobalAudioMuted(store.getState());
      if (this.globalMute !== reduxGlobalMute) {
        logger
          .remote({ system: true, capture: 'audioPool' })
          .error('AudioPool: global mute state inconsistency detected', {
            audioPoolMute: this.globalMute,
            reduxGlobalMute,
            elementId: element.id,
          });
      }

      this.applyPolicyToElement(element);

      logger
        .remote({ system: true, capture: 'audioPool' })
        .debug('AudioPool: element obtained from pool', {
          elementId: element.id,
          unlocked: element.unlocked,
          volume: element.volume,
          muted: element.muted,
          contextState: this.ctx.state,
          remainingInPool: this.pool.length,
        });

      return element;
    }

    // Pool exhausted - log detailed state and create unsafe element
    logger
      .remote({ system: true, capture: 'audioPool' })
      .warn('AudioPool: pool exhausted, creating potentially unsafe audio element', {
        poolTargetSize: this.poolSize,
        usedElements: Object.keys(this.usedElements).length,
        contextState: this.ctx.state,
        isUnlocked: this.isUnlocked,
        globalMute: this.globalMute,
        userAgent: navigator.userAgent,
      });

    // Test if we can play audio at all
    const testSound = new Audio();
    testSound.play().catch((error) => {
      logger
        .remote({ system: true, capture: 'audioPool' })
        .warn('AudioPool: test sound play failed - audio likely blocked', {
          ...formatErrorForLogging(error),
          contextState: this.ctx.state,
          isUnlocked: this.isUnlocked,
        });
    });

    const unsafeAudio = this.obtainUnsafeAudio();
    this.usedElements[unsafeAudio.id] = unsafeAudio;

    logger
      .remote({ system: true, capture: 'audioPool' })
      .warn('AudioPool: returned unsafe audio element', {
        elementId: unsafeAudio.id,
        unlocked: false,
        volume: unsafeAudio.volume,
        muted: unsafeAudio.muted,
      });

    return unsafeAudio;
  };

  private obtainUnsafeAudio = () => {
    const unsafeAudio = new Audio();

    this.tail += 1;

    unsafeAudio.id = `unsafe-audio-${this.tail}`;

    unsafeAudio.volume = this.globalMute ? 0 : 1;
    unsafeAudio.muted = this.globalMute;

    return unsafeAudio;
  };

  unloadElement = (audio: UnlockedAudio) => {
    // stop audio playback
    try {
      audio.pause();
    } catch (error) {
      logger
        .remote({ system: true, capture: 'audioPool' })
        .warn('AudioPool: error pausing audio during unload', {
          ...formatErrorForLogging(error),
          elementId: audio.id,
        });
    }

    // cleanup the source
    try {
      audio.srcObject = null;
      audio.removeAttribute('src');
    } catch (error) {
      logger
        .remote({ system: true, capture: 'audioPool' })
        .error('AudioPool: error cleaning up audio source during unload', {
          ...formatErrorForLogging(error),
          elementId: audio.id,
        });
    }

    // remove common event listeners
    audio.onended = null;
    audio.onplay = null;
    audio.oncanplaythrough = null;
    audio.onerror = null;
    audio.onloadeddata = null;
    audio.onloadedmetadata = null;

    // reload element state
    try {
      audio.load();
    } catch (error) {
      logger
        .remote({ system: true, capture: 'audioPool' })
        .warn('AudioPool: error reloading audio element during unload', {
          ...formatErrorForLogging(error),
          elementId: audio.id,
        });
    }

    audio.unloaded = true;

    delete this.usedElements[audio.id];
    // Recycled elements must never carry a stale owner tag into their next assignment.
    delete this.ownerByElementId[audio.id];

    this.releaseElement(audio);

    logger
      .remote({ system: true, capture: 'audioPool' })
      .debug('AudioPool: element unloaded successfully', {
        elementId: audio.id,
        poolSize: this.pool.length,
        usedElements: Object.keys(this.usedElements).length,
      });
  };

  muteAll = () => {
    this.globalMute = true;
    this.applyPolicy();

    logger
      .remote({ system: true, capture: 'audioPool' })
      .info('AudioPool: muted all audio elements', {
        totalElements: Object.keys(this.usedElements).length,
      });
  };

  unmuteAll = () => {
    this.globalMute = false;
    // Policy-aware: owners in locallyMutedOwners stay silenced instead of a blind volume=1.
    this.applyPolicy();

    logger
      .remote({ system: true, capture: 'audioPool' })
      .info('AudioPool: unmuted all audio elements', {
        totalElements: Object.keys(this.usedElements).length,
      });
  };

  private getState = () => ({
    contextState: this.ctx.state,
    poolSize: this.pool.length,
    unlockAttempts: this.unlockAttempts,
  });
}

export const AudioPool = new Pool();
